import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatPrice } from '../utils/currencyUtils';
import { ArrowLeft, User, Calendar, Users, MapPin, Clock, Compass, CreditCard, CheckCircle, ShoppingCart, LogOut, Building, Hotel, DollarSign, Star, Settings, Zap, Info, Trash2, AlertCircle, RefreshCw } from 'lucide-react';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../config/stripe';
import cartService from '../services/cartService';
import experienceCheckoutService from '../services/experienceCheckoutService';
import checkoutService from '../services/checkoutService';
import LoadingSpinner from '../components/LoadingSpinner';
import MainLayout from '../components/Layout/MainLayout';
import ExperiencePaymentForm from '../components/ExperiencePaymentForm';
import PromoCodeApply from '../components/checkout/PromoCodeApply';
import { toast } from 'react-hot-toast';
import { getStripeErrorMessage, isFraudError, shouldRetryPayment } from '../utils/stripeErrorMapper';
import { useCart } from '../contexts/CartContext';

const LINE_CHECKOUT_STORAGE_KEY = 'axon_experience_line_checkout';

const QA_PROMO_UI_ENABLED = import.meta.env.VITE_ENABLE_STRIPE_MIN_QA_PROMO_UI === 'true';

function restoreLineCheckoutQueueFromStorage() {
  try {
    const raw = sessionStorage.getItem(LINE_CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistLineCheckoutQueue(batchId, intents, clients, qaStripeMinPromoCode = null) {
  if (!intents?.length) {
    sessionStorage.removeItem(LINE_CHECKOUT_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    LINE_CHECKOUT_STORAGE_KEY,
    JSON.stringify({
      checkoutBatchId: batchId,
      intents,
      selectedClientsForItems: clients,
      qaStripeMinPromoCode: qaStripeMinPromoCode || null,
    })
  );
}

const ExperienceBillingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshCartCount } = useCart();
  const [experienceItems, setExperienceItems] = useState([]);
  const [selectedClientsForItems, setSelectedClientsForItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingAddress, setBillingAddress] = useState(() => {
    // Try to restore from sessionStorage on page reload (important for redirect-based payments)
    const saved = sessionStorage.getItem('axon_billing_address');
    return saved ? JSON.parse(saved) : {
      cardholderName: '',
      street: '',
      city: '',
      stateProvince: '',
      postalCode: '',
      countryCode: 'US'
    };
  });
  
  // Stripe payment states
  const [clientSecret, setClientSecret] = useState('');
  const [paymentStep, setPaymentStep] = useState('billing'); // 'billing' | 'payment' | 'success'
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [paymentError, setPaymentError] = useState(null); // Store detailed payment error
  const [canRetry, setCanRetry] = useState(true);
  const [isVerifyingRedirect, setIsVerifyingRedirect] = useState(false);

  const [pendingLinePayments, setPendingLinePayments] = useState([]);
  const [checkoutBatchId, setCheckoutBatchId] = useState(null);
  /** Total Stripe steps for current batch (for progress UI); 0 when idle */
  const [lineCheckoutTotalSteps, setLineCheckoutTotalSteps] = useState(0);

  const [qaStripeMinPromoCode, setQaStripeMinPromoCode] = useState(null);
  const [qaPromoPreview, setQaPromoPreview] = useState(null);
  const qaPromoCodeRef = useRef(null);

  const pendingLinePaymentsRef = useRef([]);
  const experienceItemsRef = useRef([]);
  const accumulatedBookingsRef = useRef([]);
  const selectedClientsRef = useRef({});
  const checkoutBatchIdRef = useRef(null);
  const initialCheckoutStepsRef = useRef(0);

  useEffect(() => {
    experienceItemsRef.current = experienceItems;
  }, [experienceItems]);
  useEffect(() => {
    pendingLinePaymentsRef.current = pendingLinePayments;
  }, [pendingLinePayments]);
  useEffect(() => {
    selectedClientsRef.current = selectedClientsForItems;
  }, [selectedClientsForItems]);
  useEffect(() => {
    checkoutBatchIdRef.current = checkoutBatchId;
  }, [checkoutBatchId]);
  useEffect(() => {
    qaPromoCodeRef.current = qaStripeMinPromoCode;
  }, [qaStripeMinPromoCode]);

  const resetBatchCheckout = () => {
    sessionStorage.removeItem(LINE_CHECKOUT_STORAGE_KEY);
    accumulatedBookingsRef.current = [];
    setPendingLinePayments([]);
    pendingLinePaymentsRef.current = [];
    setCheckoutBatchId(null);
    checkoutBatchIdRef.current = null;
    initialCheckoutStepsRef.current = 0;
    setLineCheckoutTotalSteps(0);
    setQaStripeMinPromoCode(null);
    setQaPromoPreview(null);
    qaPromoCodeRef.current = null;
  };

  const getExperienceLineDefaultTotal = (item) => {
    const participantCount = item.participantCount || 1;
    const basePricePerParticipant = item.basePrice || item.pricePerPerson || 0;
    const baseTotal = basePricePerParticipant * participantCount;
    return item.totalWithProcessingFees ?? item.price ?? baseTotal;
  };

  const getExperienceChargedDisplay = (item) => {
    if (!qaPromoPreview?.perLine || qaPromoPreview.checkoutType !== 'experience') {
      return getExperienceLineDefaultTotal(item);
    }
    const row = qaPromoPreview.perLine.find((p) => String(p.cartItemId) === String(item.id));
    return row != null ? Number(row.chargedAmount) : getExperienceLineDefaultTotal(item);
  };

  const [stripePaymentMethods, setStripePaymentMethods] = useState([]);
  const [isLoadingStripeMethods, setIsLoadingStripeMethods] = useState(true);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null); // stripePaymentMethodId or 'new'
  const [removingCardId, setRemovingCardId] = useState(null);

  // Stripe Elements options (appearance only). Do not set `currency` here when using a
  // PaymentIntent client_secret — Stripe rejects it; currency comes from the PI.
  const getStripeElementsOptions = (secret, sessionSecret) => {
    const options = {
      clientSecret: secret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0f172a',
          colorBackground: '#ffffff',
          colorText: '#0f172a',
          colorDanger: '#ef4444',
          fontFamily: 'Inter, system-ui, sans-serif',
          borderRadius: '10px',
          spacingUnit: '4px'
        },
        rules: {
          '.Input': { border: '1.5px solid #e2e8f0', boxShadow: 'none' },
          '.Input:focus': { border: '1.5px solid #0f172a', boxShadow: 'none' },
          '.Tab': { border: '1.5px solid #e2e8f0', boxShadow: 'none' },
          '.Tab--selected': { border: '1.5px solid #0f172a', boxShadow: 'none' }
        }
      }
    };

    if (sessionSecret) {
      options.customerSessionClientSecret = sessionSecret;
    }

    return options;
  };

  const countryCodes = [
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'HU', name: 'Hungary' },
    { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' },
    { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'KR', name: 'South Korea' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'EG', name: 'Egypt' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'TR', name: 'Turkey' },
    { code: 'RU', name: 'Russia' }
  ];

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        // First, try to get data from navigation state (passed from cart)
        if (location.state?.experienceItems && location.state?.selectedClientsForItems) {
          console.log('✅ Loading from navigation state:', location.state);
          setExperienceItems(location.state.experienceItems);
          setSelectedClientsForItems(location.state.selectedClientsForItems);
          setLoading(false);
          return;
        }

        // Fallback: fetch from cart if navigation state is not available
        console.log('⚠️ Navigation state not found, fetching from cart...');
        const cartResponse = await cartService.getUserCart();
        const experiences = cartResponse.data.filter(item => item.type === 'experience');
        
        const params = new URLSearchParams(location.search);
        const isRedirect = params.get('payment_intent_client_secret') || params.get('payment_intent');

        if (experiences.length === 0 && !isRedirect) {
          toast.error('No experiences found in cart');
          navigate('/cart');
          return;
        }

        setExperienceItems(experiences);

        // Load selected clients from sessionStorage as fallback
        const storedSelections = {};
        experiences.forEach(item => {
          const storedClient = sessionStorage.getItem(`selectedClient_${item.id}`);
          if (storedClient) {
            storedSelections[item.id] = JSON.parse(storedClient);
          }
        });
        setSelectedClientsForItems(storedSelections);

      } catch (err) {
        console.error('Failed to fetch experience details:', err);
        setError('Failed to load experience details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [navigate, location.state]);

  // Load saved Stripe payment methods for experience checkout
  useEffect(() => {
    const loadStripeMethods = async () => {
      setIsLoadingStripeMethods(true);
      try {
        const res = await experienceCheckoutService.getStripePaymentMethods();
        const methods = res.paymentMethods || [];
        setStripePaymentMethods(methods);
        if (methods.length > 0) {
          const defaultMethod = methods.find(m => m.isDefault);
          const initial = defaultMethod?.stripePaymentMethodId || methods[0].stripePaymentMethodId;
          setSelectedPaymentMethodId(initial);
        } else {
          setSelectedPaymentMethodId('new');
        }
      } catch (err) {
        console.error('Failed to load Stripe payment methods:', err);
        setStripePaymentMethods([]);
        setSelectedPaymentMethodId('new');
      } finally {
        setIsLoadingStripeMethods(false);
      }
    };

    loadStripeMethods();
  }, []);

  // Handle redirect return from Stripe (e.g., Alipay, Google Pay)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const piId = params.get('payment_intent');
    const piSecret = params.get('payment_intent_client_secret');
    const redirectStatus = params.get('redirect_status');

    if (piSecret && redirectStatus) {
      console.log('🔄 Detected return from redirect payment:', redirectStatus);
      
      if (redirectStatus === 'succeeded' || redirectStatus === 'processing') {
        setIsVerifyingRedirect(true);
        // Call success handler directly with the PI ID from URL if available, 
        // otherwise it will extract it from the secret
        const intentId = piId || piSecret.split('_secret')[0];
        proceedAfterLinePaid(intentId);
      } else {
        setClientSecret(piSecret);
        setPaymentStep('payment');
      }
    }
  }, [location.search]);

  const loadStripeMethods = async () => {
    try {
      const res = await experienceCheckoutService.getStripePaymentMethods();
      const methods = res.paymentMethods || [];
      setStripePaymentMethods(methods);
      if (methods.length === 0) {
        setSelectedPaymentMethodId('new');
      } else {
        const stillExists = methods.some(m => m.stripePaymentMethodId === selectedPaymentMethodId || (m._id && m._id.toString() === selectedPaymentMethodId));
        if (!stillExists) {
          const defaultMethod = methods.find(m => m.isDefault);
          setSelectedPaymentMethodId(defaultMethod?.stripePaymentMethodId || methods[0].stripePaymentMethodId);
        }
      }
    } catch (err) {
      console.error('Failed to load Stripe payment methods:', err);
      setStripePaymentMethods([]);
    }
  };

  const handleRemoveSavedCard = async (methodIdOrDbId) => {
    if (!window.confirm('Remove this card from your saved payment methods?')) return;
    setRemovingCardId(methodIdOrDbId);
    try {
      await experienceCheckoutService.removeStripePaymentMethod(methodIdOrDbId);
      toast.success('Card removed');
      const remaining = stripePaymentMethods.filter(m => (m._id && m._id.toString()) !== methodIdOrDbId && m.stripePaymentMethodId !== methodIdOrDbId);
      if (selectedPaymentMethodId === methodIdOrDbId) {
        setSelectedPaymentMethodId(remaining.length > 0 ? remaining[0].stripePaymentMethodId : 'new');
      }
      await loadStripeMethods();
    } catch (err) {
      toast.error(err.message || 'Failed to remove card');
    } finally {
      setRemovingCardId(null);
    }
  };

  const handleExperienceQaPromoApplied = async (code) => {
    const res = await checkoutService.validateQaStripeMinPromo({
      checkoutType: 'experience',
      qaStripeMinPromoCode: code
    });
    if (!res?.success || !res?.data) {
      throw new Error(res?.message || 'Could not apply promo code');
    }
    setQaStripeMinPromoCode(code);
    qaPromoCodeRef.current = code;
    setQaPromoPreview(res.data);
    toast.success('QA promo applied');
  };

  const handleExperienceQaPromoCleared = () => {
    setQaStripeMinPromoCode(null);
    qaPromoCodeRef.current = null;
    setQaPromoPreview(null);
  };

  const proceedAfterLinePaid = async (paymentIntentId) => {
    setPaymentProcessing(true);
    try {
      let queue = pendingLinePaymentsRef.current;
      const restoredFromStorage = restoreLineCheckoutQueueFromStorage();

      if (!queue?.length && restoredFromStorage?.intents?.length) {
        queue = restoredFromStorage.intents;
        pendingLinePaymentsRef.current = queue;
        setPendingLinePayments(queue);
        setCheckoutBatchId(restoredFromStorage.checkoutBatchId || null);
        checkoutBatchIdRef.current = restoredFromStorage.checkoutBatchId || null;
        initialCheckoutStepsRef.current = restoredFromStorage.intents.length;
        if (restoredFromStorage.selectedClientsForItems) {
          setSelectedClientsForItems(restoredFromStorage.selectedClientsForItems);
          selectedClientsRef.current = restoredFromStorage.selectedClientsForItems;
        }
        const pcode = restoredFromStorage.qaStripeMinPromoCode;
        if (pcode) {
          setQaStripeMinPromoCode(pcode);
          qaPromoCodeRef.current = pcode;
          try {
            const res = await checkoutService.validateQaStripeMinPromo({
              checkoutType: 'experience',
              qaStripeMinPromoCode: pcode
            });
            if (res?.success && res?.data) {
              setQaPromoPreview(res.data);
            }
          } catch (_e) {
            /* non-fatal */
          }
        }
      }

      if (!queue?.length) {
        throw new Error('Checkout session expired. Please open checkout again from your cart.');
      }

      const head = queue[0];
      if (head.paymentIntentId !== paymentIntentId) {
        throw new Error('Unexpected payment reference. Please retry from your cart.');
      }

      let items = experienceItemsRef.current;
      if (!items?.length) {
        const cartResponse = await cartService.getUserCart();
        items = cartService.normalizeCartLineItems(cartResponse).filter((item) => item.type === 'experience');
        const storedSelections = {};
        items.forEach((item) => {
          const storedClient = sessionStorage.getItem(`selectedClient_${item.id}`);
          if (storedClient) storedSelections[item.id] = JSON.parse(storedClient);
        });
        setExperienceItems(items);
        experienceItemsRef.current = items;
        setSelectedClientsForItems((prev) => ({ ...storedSelections, ...prev }));
      }

      items = experienceItemsRef.current;
      const item = items.find((i) => String(i.id) === String(head.cartItemId));
      if (!item) {
        throw new Error('Cart item not found. If you were charged, contact support.');
      }

      const clientsMap = selectedClientsRef.current;

      const response = await experienceCheckoutService.confirmBooking(
        paymentIntentId,
        [item],
        clientsMap,
        billingAddress,
        qaPromoCodeRef.current || undefined
      );

      if (!response.success) {
        throw new Error(response.message || 'Booking confirmation failed');
      }

      accumulatedBookingsRef.current = [...accumulatedBookingsRef.current, ...(response.bookings || [])];

      const restQueue = queue.slice(1);
      setPendingLinePayments(restQueue);
      pendingLinePaymentsRef.current = restQueue;

      persistLineCheckoutQueue(checkoutBatchIdRef.current, restQueue, clientsMap, qaPromoCodeRef.current);

      setExperienceItems((prev) => prev.filter((i) => String(i.id) !== String(head.cartItemId)));
      sessionStorage.removeItem(`selectedClient_${head.cartItemId}`);
      refreshCartCount();

      if (restQueue.length > 0) {
        setClientSecret(restQueue[0].clientSecret);
        setCustomerSessionClientSecret(restQueue[0].customerSessionClientSecret || '');
        toast.success(`${response.bookings?.length || 1} booked — ${restQueue.length} payment step${restQueue.length !== 1 ? 's' : ''} remaining`);
        setPaymentProcessing(false);
        setIsVerifyingRedirect(false);
        return;
      }

      sessionStorage.removeItem(LINE_CHECKOUT_STORAGE_KEY);
      setLineCheckoutTotalSteps(0);
      const allBookings = accumulatedBookingsRef.current;
      setPaymentStep('success');
      setBookingResult({
        success: true,
        bookings: allBookings,
        message: `${allBookings.length} booking${allBookings.length !== 1 ? 's' : ''} confirmed successfully`,
      });
      accumulatedBookingsRef.current = [];
      setPendingLinePayments([]);
      pendingLinePaymentsRef.current = [];
      setCheckoutBatchId(null);
      checkoutBatchIdRef.current = null;
      setClientSecret('');
      setCustomerSessionClientSecret('');
      setExperienceItems([]);
      setSelectedClientsForItems({});
      toast.success('All bookings confirmed!');
      setPaymentProcessing(false);
      setIsVerifyingRedirect(false);
      setTimeout(() => {
        navigate('/user-bookings', {
          state: { bookings: allBookings },
        });
      }, 2000);
    } catch (error) {
      console.error('Line checkout confirmation error:', error);
      toast.error(error.message || 'Payment succeeded but booking confirmation failed.');
      setPaymentProcessing(false);
      setIsVerifyingRedirect(false);
    }
  };

  const startExperienceBatchCheckout = async (paymentMethodId) => {
    accumulatedBookingsRef.current = [];
    const response = await experienceCheckoutService.createPaymentIntentsPerLine(
      experienceItems,
      billingAddress,
      selectedClientsForItems,
      paymentMethodId,
      qaStripeMinPromoCode || undefined
    );
    if (!response.success || !response.intents?.length) {
      throw new Error('Failed to initialize checkout');
    }
    initialCheckoutStepsRef.current = response.intents.length;
    setLineCheckoutTotalSteps(response.intents.length);
    setCheckoutBatchId(response.checkoutBatchId);
    checkoutBatchIdRef.current = response.checkoutBatchId;
    setPendingLinePayments(response.intents);
    pendingLinePaymentsRef.current = response.intents;
    persistLineCheckoutQueue(response.checkoutBatchId, response.intents, selectedClientsForItems, qaPromoCodeRef.current);

    const first = response.intents[0];
    setClientSecret(first.clientSecret);
    setCustomerSessionClientSecret(first.customerSessionClientSecret || '');
    return response;
  };

  const handleLoadNewCardForm = async () => {
    setPaymentError(null);
    setCustomerSessionClientSecret('');
    const allExperiencesHaveClients = experienceItems.every(item => selectedClientsForItems[item.id]);
    if (!allExperiencesHaveClients) {
      toast.error('Please assign clients to all experiences in the cart first');
      return;
    }
    if (!billingAddress.cardholderName || !billingAddress.street || !billingAddress.city || !billingAddress.stateProvince || !billingAddress.postalCode) {
      toast.error('Please fill in all billing address fields');
      return;
    }
    sessionStorage.setItem('axon_billing_address', JSON.stringify(billingAddress));
    setPaymentProcessing(true);
    try {
      const response = await startExperienceBatchCheckout(undefined);
      toast.success(
        response.intents.length > 1
          ? `Pay step 1 of ${response.intents.length} — mixed currencies are charged separately`
          : 'Select your payment method below'
      );
    } catch (error) {
      console.error('Error loading card form:', error);
      toast.error(error.message || 'Failed to load payment form.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleBillingAddressChange = (updates) => {
    setBillingAddress(prev => ({ ...prev, ...updates }));
  };

  const handlePreviousStep = () => {
    navigate('/cart');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.clear();
    navigate('/login');
  };

  const handleContinueToPayment = async () => {
    const allExperiencesHaveClients = experienceItems.every((item) => selectedClientsForItems[item.id]);
    if (!allExperiencesHaveClients) {
      toast.error('Please assign clients to all experiences in the cart first');
      return;
    }
    if (!billingAddress.cardholderName || !billingAddress.street || !billingAddress.city ||
        !billingAddress.stateProvince || !billingAddress.postalCode) {
      toast.error('Please fill in all billing address fields');
      return;
    }

    sessionStorage.setItem('axon_billing_address', JSON.stringify(billingAddress));

    const usingSavedCard = stripePaymentMethods.length > 0 && selectedPaymentMethodId && selectedPaymentMethodId !== 'new';
    if (!usingSavedCard) {
      toast('Use “Load Payment Form” to pay with a new card', { icon: '💳' });
      return;
    }

    setPaymentProcessing(true);
    setPaymentError(null);
    try {
      await startExperienceBatchCheckout(selectedPaymentMethodId);
      const stripe = await stripePromise;
      while (pendingLinePaymentsRef.current?.length > 0) {
        const queue = pendingLinePaymentsRef.current;
        const intent = queue[0];
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
          payment_method: selectedPaymentMethodId,
        });
        if (confirmError) {
          console.error('Stripe confirmCardPayment error:', confirmError);
          handlePaymentError(confirmError);
          const step = initialCheckoutStepsRef.current - queue.length + 1;
          toast.error(
            `Payment failed at step ${step} of ${initialCheckoutStepsRef.current}. ` +
            'Earlier lines are already confirmed; remaining experiences stay in your cart.',
            { duration: 8000 }
          );
          refreshCartCount();
          break;
        }
        await proceedAfterLinePaid(paymentIntent.id);
      }
    } catch (error) {
      console.error('Error in saved-card checkout:', error);
      toast.error(error.message || 'Failed to complete payment. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    await proceedAfterLinePaid(paymentIntentId);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);

    if (accumulatedBookingsRef.current.length > 0) {
      toast.error(
        'Some payments already succeeded and those bookings are confirmed. Remaining experiences stay in your cart — open checkout again to finish.',
        { duration: 9000 }
      );
    }

    // Parse the error using our error mapper
    const errorInfo = getStripeErrorMessage(error);
    const canRetryPayment = shouldRetryPayment(error);
    const isFraud = isFraudError(error);

    // Store detailed error for UI display
    setPaymentError({
      ...errorInfo,
      canRetry: canRetryPayment,
      isFraud
    });

    setCanRetry(canRetryPayment);

    // Show toast with title and message
    if (isFraud) {
      toast.error(`🚨 ${errorInfo.title}: ${errorInfo.message}`, {
        duration: 8000
      });
    } else {
      toast.error(`${errorInfo.title}: ${errorInfo.message}`, {
        duration: 6000
      });
    }
  };

  const handleRetryPayment = () => {
    resetBatchCheckout();
    setPaymentError(null);
    setCanRetry(true);
    setPaymentStep('billing');
    setClientSecret('');
    setCustomerSessionClientSecret('');

    toast('Please try again with a different payment method', {
      icon: '🔄'
    });
  };

  const calculateSummary = () => {
    if (!experienceItems || experienceItems.length === 0) return null;

    const byCurrency = {};
    experienceItems.forEach((item) => {
      const cur = item.currency || 'USD';
      if (!byCurrency[cur]) {
        byCurrency[cur] = { subtotal: 0, totalTaxesAndFees: 0, total: 0, currency: cur };
      }
      const participantCount = item.participantCount || 1;
      const basePricePerParticipant = item.basePrice || item.pricePerPerson || 0;
      const baseTotal = basePricePerParticipant * participantCount;
      const itemTotal = getExperienceChargedDisplay(item);
      byCurrency[cur].subtotal += baseTotal;
      byCurrency[cur].totalTaxesAndFees += itemTotal - baseTotal;
      byCurrency[cur].total += itemTotal;
    });

    const codes = Object.keys(byCurrency);
    const isMixed = codes.length > 1;
    if (!isMixed) {
      const g = byCurrency[codes[0]];
      return { ...g, isMixed: false, byCurrency };
    }
    return {
      isMixed: true,
      byCurrency,
      subtotal: null,
      totalTaxesAndFees: null,
      total: null,
      currency: null,
    };
  };

  const summary = calculateSummary();
  const activeLineIntent = pendingLinePayments.length > 0 ? pendingLinePayments[0] : null;
  const paymentFormAmount = activeLineIntent?.amount ?? summary?.total;
  const paymentFormCurrency = activeLineIntent?.currency ?? summary?.currency;
  const usingSavedCard = stripePaymentMethods.length > 0 && selectedPaymentMethodId && selectedPaymentMethodId !== 'new';

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
      <LoadingSpinner size="xl" />
    </div>
  );

  if (error || (experienceItems.length === 0 && paymentStep !== 'success' && !isVerifyingRedirect)) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8">
      <h2 className="text-4xl font-black text-black mb-4">ERROR</h2>
      <p className="text-slate-600 mb-6">{error || 'No experiences found'}</p>
      <button 
        onClick={() => navigate('/cart')} 
        className="px-8 py-4 bg-black text-white font-black uppercase tracking-widest text-xs rounded-xl"
      >
        Return to Cart
      </button>
    </div>
  );

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50/50">
        {/* Hero */}
        <section className="relative h-96 bg-black overflow-hidden">
          <div className='h-12'></div>
          <div className="absolute inset-0 bg-[url('https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-hsm0815-2689555.jpg')] bg-cover bg-center bg-no-repeat opacity-50"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <p className="text-sm text-slate-300 font-medium tracking-wider uppercase">Checkout Process</p>
              </div>
              <h2 className="text-4xl sm:text-5xl typography-hero-title-on-dark font-bold text-white mb-3">Billing Information</h2>
              <p className="text-slate-300 text-lg max-w-2xl mb-6">
                Review your selection and provide secure payment details
              </p>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handlePreviousStep}
                  className="bg-white/10 backdrop-blur-md px-6 py-2.5 rounded-full border border-white/20 text-sm font-bold text-white uppercase tracking-widest hover:bg-white hover:text-black transition-all"
                >
                  <ArrowLeft className="h-4 w-4 mr-2 inline" />
                  Return to Cart
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pb-12 relative z-20">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Experience Items with Read-only Client Display */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-100/50">
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
                <h2 className="text-2xl typography-section-title font-black text-slate-900 tracking-tight">
                  Your Experiences <span className="text-slate-400 font-medium ml-2">({experienceItems.length})</span>
                </h2>
                <div className="px-3 py-1 bg-slate-50 rounded-full border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Review Selection</p>
                </div>
              </div>

              {experienceItems.length > 0 ? (
                <div className="space-y-6">
                  {experienceItems.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-2xl bg-white shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-slate-300"
                    >
                      <div className="flex flex-col lg:flex-row">
                        <div className="w-full lg:w-56 h-40 lg:h-auto overflow-hidden relative">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-60" />
                        </div>

                        <div className="flex-1 p-5 flex flex-col">
                          <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl typography-card-title font-black text-slate-900 leading-tight">
                              {item.title}
                            </h3>
                          </div>

                          {/* Client Display (Read-only) */}
                          {selectedClientsForItems[item.id] ? (
                            <div className="mb-5 p-3.5 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between transition-all duration-300">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-slate-100 shadow-sm">
                                  <User className="h-4 w-4 text-slate-900" />
                                </div>
                                <div>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Assigned Guest</p>
                                  <p className="text-sm typography-body-sm font-black text-slate-900">
                                    {selectedClientsForItems[item.id].firstName} {selectedClientsForItems[item.id].lastName}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={handlePreviousStep}
                                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all duration-200"
                              >
                                Edit
                              </button>
                            </div>
                          ) : (
                            <div className="mb-5 p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center">
                              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mr-3">
                                <User className="h-4 w-4 text-amber-600" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs font-bold text-amber-800">No client assigned</p>
                                <p className="text-[10px] text-amber-600">Please assign a client in the cart first</p>
                              </div>
                              <button
                                onClick={handlePreviousStep}
                                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-600 hover:text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-all duration-200"
                              >
                                Assign
                              </button>
                            </div>
                          )}

                          {/* Experience Details */}
                          <div className="grid grid-cols-2 gap-4 mb-5 pb-5 border-b border-slate-100">
                            {item.location && (
                              <div className="space-y-0.5">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  Location
                                </span>
                                <p className="text-xs font-bold text-slate-700 truncate">
                                  {typeof item.location === 'object'
                                    ? `${item.location.city || ''}, ${item.location.country || ''}`.trim().replace(/^,\s*/, '')
                                    : item.location}
                                </p>
                              </div>
                            )}
                            {item.duration && (
                              <div className="space-y-0.5">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Duration
                                </span>
                                <p className="text-xs font-bold text-slate-700">
                                  {typeof item.duration === 'object'
                                    ? `${item.duration.days ? item.duration.days + 'd ' : ''}${item.duration.hours ? item.duration.hours + 'h ' : ''}${item.duration.minutes ? item.duration.minutes + 'm' : ''}`.trim()
                                    : item.duration}
                                </p>
                              </div>
                            )}
                            <div className="space-y-0.5">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                Participants
                              </span>
                              <p className="text-xs font-bold text-slate-700">{item.participantCount || 1}</p>
                            </div>
                            {item.selectedDate && (
                              <div className="space-y-0.5">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Date
                                </span>
                                <p className="text-xs font-bold text-slate-700">{item.selectedDate}</p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                            <div className="flex items-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                              <Compass className="h-3 w-3 mr-2 text-slate-300" />
                              Experience
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-400 font-medium leading-none mb-1">Total Price</p>
                              <p className="text-xl typography-body font-black text-slate-900">
                                {formatPrice(getExperienceChargedDisplay(item), item.currency)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No experiences found</h3>
                  <p className="text-slate-500 mb-6">Add some experiences to get started</p>
                  <button
                    onClick={() => navigate('/experiences')}
                    className="inline-flex items-center px-6 py-3 text-white font-medium bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors duration-200"
                  >
                    <Compass className="h-5 w-5 mr-2" />
                    Browse Experiences
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right: Billing Address & Summary */}
          <div className="lg:col-span-2">
            <div className="sticky top-10">
              <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-100/50 p-6 overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-slate-900 to-slate-700" />
                <h2 className="text-2xl typography-section-title font-black text-slate-900 mb-6 tracking-tight">Billing Details</h2>

                {/* Billing Address Form */}
                <div className="space-y-5 mb-8">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                      Cardholder Name
                    </label>
                    <input
                      type="text"
                      value={billingAddress.cardholderName}
                      onChange={(e) => handleBillingAddressChange({ cardholderName: e.target.value })}
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={billingAddress.street}
                      onChange={(e) => handleBillingAddressChange({ street: e.target.value })}
                      className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                      placeholder="123 Main St"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={billingAddress.city}
                        onChange={(e) => handleBillingAddressChange({ city: e.target.value })}
                        className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        State/Province
                      </label>
                      <input
                        type="text"
                        value={billingAddress.stateProvince}
                        onChange={(e) => handleBillingAddressChange({ stateProvince: e.target.value })}
                        className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                        placeholder="State"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={billingAddress.postalCode}
                        onChange={(e) => handleBillingAddressChange({ postalCode: e.target.value })}
                        className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                        placeholder="12345"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                        Country
                      </label>
                      <select
                        value={billingAddress.countryCode}
                        onChange={(e) => handleBillingAddressChange({ countryCode: e.target.value })}
                        className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 transition-all duration-300 text-sm font-medium"
                      >
                        {countryCodes.map(country => (
                          <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {QA_PROMO_UI_ENABLED && !clientSecret && experienceItems.length > 0 && (
                  <div className="mb-6">
                    <PromoCodeApply
                      checkoutType="experience"
                      disabled={paymentProcessing}
                      onApplied={handleExperienceQaPromoApplied}
                      onCleared={handleExperienceQaPromoCleared}
                    />
                  </div>
                )}

                {/* Action Section to Load Payment Form */}
                {!clientSecret && (
                  <div className="mb-8 p-6 bg-slate-50 border border-slate-200 rounded-2xl">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <p className="text-sm font-bold text-slate-900 mb-1 uppercase tracking-widest">
                            Complete Booking
                          </p>
                          <p className="text-xs text-slate-500">
                            Mixed currencies are charged in separate payment steps. Use a saved card to pay all steps in one flow, or load the secure form for a new card or wallets.
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                        <button
                          type="button"
                          onClick={handleLoadNewCardForm}
                          disabled={paymentProcessing}
                          className="px-6 py-3 bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-slate-900/20"
                        >
                          {paymentProcessing ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4" />
                              Load Payment Form
                            </>
                          )}
                        </button>
                        {usingSavedCard && (
                          <button
                            type="button"
                            onClick={handleContinueToPayment}
                            disabled={paymentProcessing}
                            className="px-6 py-3 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 text-xs font-black uppercase tracking-widest rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            Pay with saved card
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}


                {/* Payment Error Banner */}
                {paymentError && (
                  <div className={`mb-6 border-2 rounded-2xl p-6 ${paymentError.isFraud
                      ? 'bg-red-50 border-red-300'
                      : paymentError.severity === 'error'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${paymentError.isFraud || paymentError.severity === 'error'
                          ? 'bg-red-100'
                          : 'bg-yellow-100'
                        }`}>
                        <AlertCircle className={`h-6 w-6 ${paymentError.isFraud || paymentError.severity === 'error'
                            ? 'text-red-600'
                            : 'text-yellow-600'
                          }`} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`text-lg font-bold mb-2 ${paymentError.isFraud || paymentError.severity === 'error'
                            ? 'text-red-900'
                            : 'text-yellow-900'
                          }`}>
                          {paymentError.isFraud ? '🚨 ' : ''}{paymentError.title}
                        </h4>
                        <p className={`text-sm mb-2 ${paymentError.isFraud || paymentError.severity === 'error'
                            ? 'text-red-700'
                            : 'text-yellow-700'
                          }`}>
                          {paymentError.message}
                        </p>
                        <p className={`text-sm font-medium ${paymentError.isFraud || paymentError.severity === 'error'
                            ? 'text-red-800'
                            : 'text-yellow-800'
                          }`}>
                          💡 {paymentError.guidance}
                        </p>
                        {paymentError.canRetry && (
                          <button
                            onClick={handleRetryPayment}
                            className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${paymentError.isFraud || paymentError.severity === 'error'
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                              }`}
                          >
                            <RefreshCw className="h-4 w-4" />
                            Try Different Payment Method
                          </button>
                        )}
                        {!paymentError.canRetry && (
                          <div className="mt-4 p-3 bg-white/60 rounded-lg border border-red-200">
                            <p className="text-xs text-red-700">
                              <strong>⚠️ Unable to retry:</strong> Please contact support for assistance.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Inline payment form */}
                {paymentStep === 'billing' && clientSecret && (
                  <div className="mb-8 border border-slate-200 rounded-2xl p-6 bg-white shadow-sm">
                    {lineCheckoutTotalSteps > 1 && (
                      <div className="mb-6 rounded-xl bg-slate-900 text-white px-4 py-3">
                        <p className="text-xs font-black uppercase tracking-widest">
                          Payment {lineCheckoutTotalSteps - pendingLinePayments.length + 1} of {lineCheckoutTotalSteps}
                        </p>
                        {activeLineIntent ? (
                          <p className="text-sm font-bold mt-2">
                            This step: {formatPrice(activeLineIntent.amount, activeLineIntent.currency)}
                          </p>
                        ) : null}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-6">
                      <CreditCard className="h-5 w-5 text-slate-900" />
                      <h3 className="text-lg typography-card-title font-bold text-slate-900">Payment Details</h3>
                    </div>
                    <Elements
                      key={`stripe-pi-${clientSecret}`}
                      stripe={stripePromise}
                      options={getStripeElementsOptions(clientSecret, customerSessionClientSecret)}
                    >
                      <ExperiencePaymentForm
                        clientSecret={clientSecret}
                        billingAddress={billingAddress}
                        onSuccess={(paymentIntentId) => handlePaymentSuccess(paymentIntentId)}
                        onError={handlePaymentError}
                        amount={paymentFormAmount}
                        currency={paymentFormCurrency}
                      />
                    </Elements>
                    <p className="mt-4 text-xs text-slate-500 text-center">
                      Choosing a payment method will automatically include your saved cards.
                    </p>
                  </div>
                )}


                {/* Stripe Payment Form (when navigated via Continue to Payment – saved card path) */}
                {paymentStep === 'payment' && clientSecret && (
                  <div className="mb-8">
                    {lineCheckoutTotalSteps > 1 && (
                      <div className="mb-4 rounded-xl bg-slate-900 text-white px-4 py-3">
                        <p className="text-xs font-black uppercase tracking-widest">
                          Payment {lineCheckoutTotalSteps - pendingLinePayments.length + 1} of {lineCheckoutTotalSteps}
                        </p>
                        {activeLineIntent ? (
                          <p className="text-sm font-bold mt-2">
                            This step: {formatPrice(activeLineIntent.amount, activeLineIntent.currency)}
                          </p>
                        ) : null}
                      </div>
                    )}
                    <h3 className="text-xl typography-card-title font-black text-slate-900 mb-4">Complete Payment</h3>
                    <Elements
                      key={`stripe-pi-${clientSecret}`}
                      stripe={stripePromise}
                      options={getStripeElementsOptions(clientSecret, customerSessionClientSecret)}
                    >
                      <ExperiencePaymentForm
                        clientSecret={clientSecret}
                        billingAddress={billingAddress}
                        onSuccess={(paymentIntentId) => handlePaymentSuccess(paymentIntentId)}
                        onError={handlePaymentError}
                        amount={paymentFormAmount}
                        currency={paymentFormCurrency}
                      />
                    </Elements>
                    <p className="mt-3 text-xs text-slate-500">
                      Want to save a card for future use?{' '}
                      <a href="/bank-account" className="text-slate-700 font-semibold underline hover:text-black">
                        Add it from your Payment Methods page
                      </a>
                    </p>
                  </div>
                )}

                {/* Redirect Verification Loading State */}
                {isVerifyingRedirect && (
                  <div className="mb-8 p-12 bg-white border-2 border-slate-100 rounded-3xl shadow-xl shadow-slate-200/50 flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mb-6 animate-pulse">
                      <RefreshCw className="h-10 w-10 text-white animate-spin" />
                    </div>
                    <h3 className="text-2xl typography-section-title font-black text-slate-900 mb-3">
                      Verifying Your Payment
                    </h3>
                    <p className="text-slate-500 max-w-sm">
                      We're confirming your transaction with Stripe and finalizing your booking. 
                      Please don't refresh the page.
                    </p>
                  </div>
                )}

                {/* Success State */}
                {paymentStep === 'success' && (
                  <div className="mb-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg typography-card-title font-black text-slate-900 mb-1">
                          Payment Successful!
                        </h3>
                        <p className="text-sm text-slate-600">
                          {bookingResult?.message || 'Your bookings have been confirmed'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Summary */}
                {summary && (
                  <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-slate-100/50 rounded-full -mr-12 -mt-12" />
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Cost Breakdown</p>
                    <div className="space-y-3 relative z-10">
                      {summary.isMixed ? (
                        <>
                          <p className="text-[11px] text-slate-600 mb-2">
                            Totals are shown per currency — mixed currencies are not combined into one figure.
                          </p>
                          {Object.entries(summary.byCurrency).map(([code, grp]) => (
                            <div key={code} className="border border-slate-200 rounded-xl p-4 bg-white space-y-2">
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{code}</p>
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Base Amount</span>
                                <span className="text-sm font-bold text-slate-900">{formatPrice(grp.subtotal, grp.currency)}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Taxes & Fees</span>
                                <span className="text-sm font-bold text-slate-900">{formatPrice(grp.totalTaxesAndFees, grp.currency)}</span>
                              </div>
                              <div className="flex justify-between items-end pt-2 border-t border-slate-100">
                                <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Total ({code})</span>
                                <span className="text-lg font-black text-slate-900">{formatPrice(grp.total, grp.currency)}</span>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Base Amount</span>
                            <span className="text-sm font-bold text-slate-900">{formatPrice(summary.subtotal, summary.currency)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-tight">Taxes & Fees</span>
                            <span className="text-sm font-bold text-slate-900">{formatPrice(summary.totalTaxesAndFees, summary.currency)}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-3 mt-3">
                            <div className="flex justify-between items-end">
                              <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Total Payable</span>
                              <div className="text-right">
                                <span className="text-2xl typography-section-title font-black text-slate-900">{formatPrice(summary.total, summary.currency)}</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons - Only show Back to Cart initially */}
                {paymentStep === 'billing' && !clientSecret && (
                  <div className="flex items-center gap-4 mt-8">
                    <button
                      onClick={handlePreviousStep}
                      className="flex-1 px-6 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-900 border border-slate-100 hover:border-slate-900 rounded-2xl transition-all duration-300"
                    >
                      Back to Cart
                    </button>
                    {/* The primary button is now the 'Load Payment Form' inside the card above */}
                  </div>
                )}

                {paymentStep === 'billing' && clientSecret && (
                   <div className="flex items-center gap-4 mt-4">
                     <button
                       type="button"
                       onClick={() => {
                         resetBatchCheckout();
                         setClientSecret('');
                         setCustomerSessionClientSecret('');
                       }}
                       className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-2"
                     >
                       <span className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
                       Edit Billing Address
                     </button>
                   </div>
                )}

                {paymentStep === 'payment' && (
                  <div className="flex items-center gap-4 mt-8">
                    <button
                      onClick={() => {
                        resetBatchCheckout();
                        setPaymentStep('billing');
                        setClientSecret('');
                        setCustomerSessionClientSecret('');
                      }}
                      disabled={paymentProcessing}
                      className="flex-1 px-6 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-900 border border-slate-100 hover:border-slate-900 rounded-2xl transition-all duration-300 disabled:opacity-50"
                    >
                      Back to Billing
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
      </div>
    </MainLayout>
  );
};

export default ExperienceBillingPage;
